import Cookies from 'js-cookie';
import { AppStyle } from './appStyle';
import { appStyleMode } from './clientConfigs';

const accessTokenKey: string = 'access';
const refreshTokenKey: string = 'refresh';
const anonymousIdKey: string = 'anonymous-id';
const omnisendTokenIdKey: string = 'omnisendContactID';

export interface ITokenStorage {
  getAccessToken: () => string;
  getRefreshToken: () => string;
  update: (accessToken: string, refreshToken: string, remember: boolean) => void;
  getAnonymousToken: () => string;
  getOmnisendToken: () => string;
  clearAnonymousToken: () => void;
  clear: () => void;
}

// RFC4122 v4 GUID
function generateGuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export class TokenStorage {
  private static _instance: ITokenStorage;

  public static get Instance() {
    if (this._instance != null) {
      return this._instance;
    }

    if (appStyleMode === AppStyle.Mobile) {
      this._instance = new TokenLocalStorage();
    } else {
      this._instance = new TokenCookieStorage();
    }

    return this._instance;
  }
}

class TokenCookieStorage implements ITokenStorage {
  getAccessToken() {
    return Cookies.get(accessTokenKey);
  }

  getRefreshToken() {
    return Cookies.get(refreshTokenKey);
  }

  update(accessToken: string, refreshToken: string, remember: boolean) {
    if (accessToken && refreshToken) {
      const expires = remember ? 7 : null;
      Cookies.set(accessTokenKey, accessToken, { secure: true, expires: expires });
      Cookies.set(refreshTokenKey, refreshToken, { secure: true, expires: expires });
    } else {
      this.clear();
    }
  }

  // Ensures anonymous-id cookie exists; creates GUID if missing
  getAnonymousToken() {
    let id = Cookies.get(anonymousIdKey);
    if (!id) {
      id = generateGuid();
      Cookies.set(anonymousIdKey, id, { secure: true, expires: 7 });
    }

    return id;
  }

  getOmnisendToken() {
    return Cookies.get(omnisendTokenIdKey);
  }

  clearAnonymousToken() {
    Cookies.remove(anonymousIdKey);
  }

  clear() {
    Cookies.remove(accessTokenKey);
    Cookies.remove(refreshTokenKey);
  }
}

class TokenLocalStorage implements ITokenStorage {
  getAccessToken() {
    return localStorage.getItem(accessTokenKey);
  }

  getRefreshToken() {
    return localStorage.getItem(refreshTokenKey);
  }

  update(accessToken: string, refreshToken: string, _remember: boolean) {
    if (accessToken && refreshToken) {
      localStorage.setItem(accessTokenKey, accessToken);
      localStorage.setItem(refreshTokenKey, refreshToken);
    } else {
      this.clear();
    }
  }

  // Mirrors cookie behavior for mobile/local storage
  getAnonymousToken() {
    let id = localStorage.getItem(anonymousIdKey);
    if (!id) {
      id = generateGuid();
      localStorage.setItem(anonymousIdKey, id);
    }

    return id;
  }

  getOmnisendToken() {
    return localStorage.getItem(omnisendTokenIdKey);
  }

  clearAnonymousToken() {
    localStorage.removeItem(anonymousIdKey);
  }

  clear() {
    localStorage.removeItem(accessTokenKey);
    localStorage.removeItem(refreshTokenKey);
  }
}
